import * as React from "react";
import { Medewerker } from "../../../entities/Medewerker";
import { Select, IOption } from "../../atoms/forms/Select";
import { IApplicationState } from "../../../store";
import * as Selectie from "../../../store/selectie";
import { FetchState } from "../../../entities/FetchState";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

interface IMedewerker extends IOption {
    medewerker: Medewerker;
}

const mapMedewerkerToValue = (medewerker: Medewerker): IMedewerker => {
    return {
        value: medewerker.id,
        label: medewerker.fullName,
        medewerker: medewerker
    }
}

// New simplified props interface
interface IProps {
    label?: string;
    selectedMedewerker?: Medewerker;
    loading?: boolean;
    syncWithRedux?: boolean; // Optional: sync selection with Redux state
    showOnlyActive?: boolean; // New prop to filter for active medewerkers only
    onChange?: (medewerker?: Medewerker) => void;
}

// Actual Component
export const MedewerkerSelect = (props: IProps) => {
    const { t: tDatatypes } = useTranslation('datatypes');
    
    // Get Redux state and dispatch
    const medewerkersList = useSelector((state: IApplicationState) => state.selectie.allMedewerkers);
    const reduxSelectedMedewerker = useSelector((state: IApplicationState) => state.selectie.selectedMedewerker);
    const dispatch = useDispatch();

    // Internal state for selected medewerker
    const [selectedMedewerker, setSelectedMedewerker] = React.useState<IMedewerker | null>(() => {
        // Initialize with props value or Redux value if syncing
        const initialMedewerker = props.selectedMedewerker || (props.syncWithRedux ? reduxSelectedMedewerker : null);
        return initialMedewerker ? mapMedewerkerToValue(initialMedewerker) : null;
    });

    // Fetch medewerkers if not loaded
    React.useEffect(() => {
        if (medewerkersList.fetchState === FetchState.NotFetched) {
            dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers());
        }
    }, [dispatch, medewerkersList.fetchState]);

    // Sync with Redux selected medewerker if syncWithRedux is enabled
    React.useEffect(() => {
        if (props.syncWithRedux && reduxSelectedMedewerker) {
            setSelectedMedewerker(mapMedewerkerToValue(reduxSelectedMedewerker));
        }
    }, [props.syncWithRedux, reduxSelectedMedewerker]);

    // Update internal state when props change
    React.useEffect(() => {
        if (props.selectedMedewerker) {
            setSelectedMedewerker(mapMedewerkerToValue(props.selectedMedewerker));
        }
    }, [props.selectedMedewerker]);

    // Filter medewerkers based on active status if showOnlyActive is enabled
    const medewerkers = React.useMemo(() => {
        return medewerkersList.medewerkers
            .filter(medewerker => !props.showOnlyActive || medewerker.isActive) // Filter by active status
            .map(mapMedewerkerToValue);
    }, [medewerkersList.medewerkers, props.showOnlyActive]);

    const updateMedewerker = (medewerker: IMedewerker | null) => {
        setSelectedMedewerker(medewerker);
        
        // Update Redux state if syncing is enabled
        if (props.syncWithRedux && medewerker) {
            dispatch(Selectie.SelectieActionCreator.ActiveMedewerkerChanged(medewerker.medewerker));
        }
        
        // Call onChange callback
        props.onChange?.(medewerker?.medewerker);
    }

    return (<Select 
        label={(props.label || tDatatypes('medewerkerLabel') || "Medewerker") + (medewerkersList.fetchState !== FetchState.Success ? " bezig met laden..." : "")}
        placeholder={tDatatypes('medewerkerPlaceholder')}
        options={medewerkers}
        value={selectedMedewerker}
        disabled={medewerkersList.fetchState !== FetchState.Success}
        onChange={updateMedewerker}
        loading={medewerkersList.fetchState === FetchState.Busy || medewerkersList.fetchState === FetchState.NotFetched || props.loading}
        error={medewerkersList.fetchState === FetchState.Error ? [true, `Er was een fout tijdens het laden van de gegevens, druk op F5 om verder te gaan (${medewerkersList.errorMessage})`] : undefined}	
    />)
}

// Convenience components for common use cases
export const ActiveMedewerkerSelect = (props: Omit<IProps, 'syncWithRedux'>) => {
    return <MedewerkerSelect {...props} syncWithRedux={true} showOnlyActive={true} />;
};

export const AllMedewerkerSelect = (props: Omit<IProps, 'syncWithRedux'>) => {
    return <MedewerkerSelect {...props} syncWithRedux={true} />;
};

// Legacy exports for backward compatibility
export type AllProps = IProps;

// Legacy mapping - kept for any existing usage but deprecated
export const mapAlleMedewerkersToProps = {
    state: (state: IApplicationState) => ({
        medewerkers: state.selectie.allMedewerkers,
        selectedMedewerker: state.selectie.selectedMedewerker
    }),
    dispatch: (dispatch: any) => ({
        fetchMedewerkers: () => dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers()),
        setSelectedMedewerker: (medewerker: Medewerker) => dispatch(Selectie.SelectieActionCreator.ActiveMedewerkerChanged(medewerker))
    })
}
